import Joi from 'joi';

import { TaxRateTypeEnum } from '../../../../shared/enums/tax-rate-type.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleInsuranceCoverageTypeEnum } from '../../enums/vehicle-insurance-coverage-type.enum';
import { VehicleInsurancePremiumPeriodEnum } from '../../enums/vehicle-insurance-premium-period.enum';
import { VehicleInsuranceRecordCategoryEnum } from '../../enums/vehicle-insurance-record-category.enum';

/**
 * Schema for new vehicle insurance record validation.
 */
export const createVehicleInsuranceSchema = () =>
  Joi.object({
    code: Joi.valid(null).optional().messages({
      'any.only': 'validations|vehicle-insurance-code|must-be-null',
    }),

    recordCategory: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-insurance-record-category|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleInsuranceRecordCategoryEnum))
          .required()
          .messages({
            'any.required':
              'validations|vehicle-insurance-record-category|required',
            'any.only':
              'validations|vehicle-insurance-record-category|invalid-value',
            'string.base':
              'validations|vehicle-insurance-record-category|must-be-string',
            'string.empty':
              'validations|vehicle-insurance-record-category|cannot-be-empty',
          }),
      })
      .required()
      .messages({
        'any.required':
          'validations|vehicle-insurance-record-category|required',
      }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleInsuranceRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives()
        .conditional(Joi.any().valid(null), {
          then: Joi.invalid(null).messages({
            'any.invalid':
              'validations|vehicle-insurance-vehicle-id|cannot-be-null',
          }),
          otherwise: Joi.number().required().messages({
            'any.required': 'validations|vehicle-insurance-vehicle-id|required',
            'number.base':
              'validations|vehicle-insurance-vehicle-id|must-be-number',
          }),
        })
        .required()
        .messages({
          'any.required': 'validations|vehicle-insurance-vehicle-id|required',
        }),
      otherwise: Joi.allow(null),
    }),

    supplierId: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-supplier-id|must-be-number',
    }),

    policyNumber: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-policy-number|must-be-number',
    }),

    coverNoteNumber: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-cover-note-number|must-be-number',
    }),

    insuredValue: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-insured-value|must-be-number',
    }),

    premiumPeriod: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-insurance-premium-period|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleInsurancePremiumPeriodEnum))
          .required()
          .messages({
            'any.required':
              'validations|vehicle-insurance-premium-period|required',
            'any.only':
              'validations|vehicle-insurance-premium-period|invalid-value',
            'string.base':
              'validations|vehicle-insurance-premium-period|must-be-string',
            'string.empty':
              'validations|vehicle-insurance-premium-period|cannot-be-empty',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|vehicle-insurance-premium-period|required',
      }),

    excessAmount: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-excess-amount|must-be-number',
    }),

    totalLossPercentage: Joi.number().min(0).max(100).allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-total-loss-percentage|must-be-number',
      'number.min':
        'validations|vehicle-insurance-total-loss-percentage|cannot-be-less-than-zero',
      'number.max':
        'validations|vehicle-insurance-total-loss-percentage|cannot-exceed-100',
    }),

    computedTotalLossAmountReceivable: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-insurance-computed-total-loss-amount-receivable|must-be-number',
    }),

    amount: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-amount|must-be-number',
    }),

    totalNet: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-net|must-be-number',
    }),

    totalTax: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-tax|must-be-number',
    }),

    totalGross: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-total-gross|must-be-number',
    }),

    coverageType: Joi.string()
      .valid(...Object.values(VehicleInsuranceCoverageTypeEnum))
      .empty(null)
      .default(VehicleInsuranceCoverageTypeEnum.THIRD_PARTY)
      .messages({
        'any.only': 'validations|vehicle-insurance-coverage-type|invalid-value',
      }),

    coverageDescription: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-insurance-coverage-description|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-insurance-external-reference|must-be-string',
    }),

    taxRateType: Joi.string()
      .valid(...Object.values(TaxRateTypeEnum))
      .empty(null)
      .default(TaxRateTypeEnum.STANDARD_TAX_RATE)
      .messages({
        'any.only': 'validations|vehicle-insurance-tax-rate-type|invalid-value',
      }),

    taxRate: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-insurance-tax-rate|must-be-number',
    }),

    isTaxIncluded: Joi.boolean().empty(null).default(false),

    startsAt: Joi.date().required().messages({
      'any.required': 'validations|vehicle-insurance-starts-at|required',
      'date.base':
        'validations|vehicle-insurance-starts-at|must-be-a-valid-date',
    }),

    endsAt: Joi.date().greater(Joi.ref('startsAt')).required().messages({
      'any.required': 'validations|vehicle-insurance-ends-at|required',
      'date.base': 'validations|vehicle-insurance-ends-at|must-be-a-valid-date',
      'date.greater':
        'validations|vehicle-insurance-ends-at|cannot-be-before-starts-at',
    }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-insurance-tags|invalid-value',
    }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-insurance-notes|must-be-string',
    }),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
